import { useAnalytics } from '@/contexts/analytics-context/use-analytics';
import { BASE_API_PATH } from '@/contexts/data-context/data-provider';
import { useData } from '@/contexts/data-context/use-data';
import { ErrorSection } from '@/design-system/error-section/error-section';
import { LoadingSection } from '@/design-system/loading/loading';
import { Stack } from '@/design-system/stack/stack';
import { isInvestmentAccount } from '@/utils/accounts/is-investment-account';
import { format } from '@/utils/dates';
import { exhaustiveCheck } from '@/utils/exhaustive-check';
import { getCountryCurrency } from '@/utils/get-country-currency';
import { CompanyRequestType } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { InvestmentHoldingType, PortfolioValuationType } from 'PublicEntityTypes';
import { ComponentProps } from 'react';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import { TreasuryFlow } from './treasury-flow';
import { HoldingDetails } from './types/treasury-types';
import { getReturnData } from './utils/get-return-data';
import { getTreasuryFlowStep } from './utils/get-treasury-flow-step';

export const ANNUAL_FEE_PERCENTAGE = 0.25;
const PORTFOLIO_VALUATION_URL = `${BASE_API_PATH}/portfoliovaluation`;
const INVESTMENT_HOLDINGS_URL = '/api/treasury/investment-holdings';
const BOOK_A_CALL_URL = 'https://calendly.com/jack-seapoint/15-minute-onboarding?back=1';
export const TREASURY_HELP_LINK = 'https://www.notion.so/Treasury-2ae278491bc08042a991f4cdecdacc6a';
const PAYMENTS_URL = '/payments';
const DATE_FORMAT = 'dd MMMM yyyy';

export const TreasuryPage = () => {
  const navigate = useNavigate();
  const { isEuCompanyWithTreasury } = useFlags();
  const analytics = useAnalytics();

  const { data, isLoading: isLoadingData, error: dataError } = useData();
  const {
    data: portfolioValuation,
    isLoading: isLoadingPortfolioValuation,
    error: portfolioValuationError,
  } = useSWR<PortfolioValuationType[]>(PORTFOLIO_VALUATION_URL);
  const {
    data: investmentHoldingsData,
    isLoading: isLoadingInvestmentHoldingsData,
    error: investmentHoldingsError,
  } = useSWR<InvestmentHoldingType[]>(INVESTMENT_HOLDINGS_URL);

  if (isLoadingData || isLoadingPortfolioValuation || isLoadingInvestmentHoldingsData) {
    return <LoadingSection text="Loading..." />;
  }

  if (!data || !portfolioValuation || !investmentHoldingsData) {
    return <ErrorSection error={dataError || portfolioValuationError || investmentHoldingsError} />;
  }

  const { companies, accounts } = data;

  const userCompany = companies[0];
  const userCompanyCurrency = getCountryCurrency(userCompany.legalEntity.country);
  const investmentHoldingInCompanyCurrency = investmentHoldingsData.find((h) => h.currency === userCompanyCurrency);

  if (!investmentHoldingInCompanyCurrency) {
    Sentry.captureMessage('Investment holding not found in TreasuryPage');

    return <ErrorSection />;
  }

  const investmentAccount = accounts.find(isInvestmentAccount);
  const step = getTreasuryFlowStep({ company: userCompany, investmentAccount, isEuCompanyWithTreasury });
  const trimmedInvestmentHolding = trimInvestmentHoldingObject(investmentHoldingInCompanyCurrency);

  const returnData = getReturnData(portfolioValuation, investmentHoldingInCompanyCurrency.currency);

  let treasuryFlowConfig: ComponentProps<typeof TreasuryFlow>['treasuryFlowConfig'];

  switch (step) {
    case 'EMPTY_STATE_EU_WAIT_LIST': {
      const hasRecordedInterest =
        userCompany.requests?.some((request) => request.type === CompanyRequestType.NON_GB_TREASURY) ?? false;

      treasuryFlowConfig = {
        step: 'EMPTY_STATE_EU_WAIT_LIST',
        hasRecordedInterest,
      };

      break;
    }
    case 'EMPTY_STATE_NO_KIID': {
      treasuryFlowConfig = {
        step: 'EMPTY_STATE_NO_KIID',
        holdingDetails: trimmedInvestmentHolding,
        companyId: userCompany.id,
        isin: investmentHoldingInCompanyCurrency.isin,
        onContinue: () => {
          analytics.track('treasury_onboarding_started', {
            companyLegalEntityCountry: companies[0].legalEntity.country,
          });
        },
      };

      break;
    }
    case 'EMPTY_STATE_NO_ACCOUNT':
      treasuryFlowConfig = {
        step: 'EMPTY_STATE_NO_ACCOUNT',
        onBookCall: () => window.open(BOOK_A_CALL_URL, '_blank', 'noopener noreferrer'),
      };

      break;
    case 'EMPTY_STATE_PENDING':
      treasuryFlowConfig = {
        step: 'EMPTY_STATE_PENDING',
      };

      break;
    case 'APPROVED': {
      if (!investmentAccount) {
        Sentry.captureMessage('Investment account not found in TreasuryPage');

        treasuryFlowConfig = {
          step: 'EMPTY_STATE_PENDING',
        };

        break;
      }

      treasuryFlowConfig = {
        step: 'APPROVED',
        account: investmentAccount,
        holdingDetails: trimmedInvestmentHolding,
        returnData,
        onInitiateDeposit: () => {
          navigate(`${PAYMENTS_URL}?action=INTERNAL_PAYMENT`, {
            state: {
              isFromDepositTreasuryPageConfig: {
                isFromDepositTreasuryPage: true,
                investmentAccountId: investmentAccount.id,
              },
            },
          });
        },
        onInitiateWithdrawal: () => {
          navigate(`${PAYMENTS_URL}?action=INTERNAL_PAYMENT`, {
            state: {
              isFromWithdrawalTreasuryPageConfig: {
                isFromWithdrawalTreasuryPage: true,
                investmentAccountId: investmentAccount.id,
              },
            },
          });
        },
      };

      break;
    }
    default:
      return exhaustiveCheck(step);
  }

  return (
    <Stack align="stretch" className="min-h-screen py-2 pr-2">
      <Stack align="stretch" className="flex-1 overflow-clip rounded-md border border-border bg-background shadow-xs">
        <Stack align="stretch" className="w-full flex-1">
          <TreasuryFlow treasuryFlowConfig={treasuryFlowConfig} />
        </Stack>
      </Stack>
    </Stack>
  );
};

const trimInvestmentHoldingObject = (holding: InvestmentHoldingType): HoldingDetails => {
  return {
    name: holding.name,
    currency: holding.currency,
    estimatedYield: holding.estimatedYield,
    netYield: holding.estimatedYield - ANNUAL_FEE_PERCENTAGE,
    moreInfoLink: holding.moreInfoLink,
    kiidLink: holding.kiidLink,
    prospectusLink: holding.prospectusLink,
    formattedUpdatedAtByFundManager: format(holding.updatedAtByFundManager, DATE_FORMAT),
  };
};
